import { NacreError, NacreTransportError, type Problem } from './errors.js'
import type {
  AuditPage,
  CeilingValue,
  AuditQuery,
  AuditRecord,
  CreatedServiceAccount,
  CreatedUser,
  Document,
  EmbeddingProvider,
  Grant,
  GrantInput,
  Group,
  GroupMember,
  IngestOutcome,
  IngestRequest,
  Job,
  Layer,
  LayerInput,
  Permission,
  RecallCheck,
  ReferenceQuery,
  ReferenceQueryInput,
  ReindexStatus,
  BegunSecondFactor,
  ConfirmedSecondFactor,
  SecondFactor,
  SecondFactorEnrolmentRequired,
  SignIn,
  Tokens,
  Workspace,
  SearchHit,
  SearchOptions,
  Connection,
  Proposal,
  ProposalOutcome,
  Self,
  Endpoints,
  ServiceAccount,
  User,
  UserRole,
  SecondFactorKind,
  WebAuthnAssertion,
  WebAuthnAssertionOptions,
  WebAuthnRegistrationOptions,
  UploadDescriptor,
  UploadTicketRequest,
  BaseSkill,
  SkillEntry,
  SkillFiles,
  SkillLevel,
  SkillVersion,
  SkillWrite,
} from './types.js'

/**
 * The TypeScript client.
 *
 * Two decisions shape the whole surface, and both are about the permission
 * model rather than about ergonomics.
 *
 * **No method takes an organization.** Not as an option, not as an override for
 * administrators, not anywhere. Invariant I1 says the organization comes from
 * the token, and the server refuses a request that names one with a 403 — which
 * from inside an application reads as a bug in this library. Making it
 * unrepresentable is cheaper than explaining it.
 *
 * **`get`-shaped methods answer `undefined` for 404 rather than throwing.** The
 * API returns the same 404, with the same wording, for "absent" and for "not
 * yours" (invariant I4). A client that surfaced those as an exception would
 * invite a `catch` that treats one as retryable and the other as fatal, and
 * there is no information here to tell them apart. `undefined` says exactly
 * what the server said.
 *
 * Zero dependencies. `fetch` is in every runtime this targets, and a client for
 * a security product is the wrong place to inherit a dependency tree.
 */

export interface ClientOptions {
  /** Where the API lives, e.g. `https://api.nacre.work`. No trailing `/v1`. */
  readonly baseUrl: string
  /**
   * A JWT from `init`, or a `nacre_sk_` service account key. The client does
   * not care which — both go in the same header, and the server distinguishes
   * them. The organization is inside it, which is why no method takes one.
   */
  readonly token: string
  /** Per-request timeout in milliseconds. Default 30 seconds. */
  readonly timeoutMs?: number
  /**
   * Attempts for a transient failure (429, 503, 5xx). Default 2, meaning one
   * retry. Only ever applied to safe methods and to ingest, which is idempotent
   * on `(layer, external_id)` and its content hash — retrying a delete or a
   * grant would be a second, different write.
   */
  readonly retries?: number
  /** Swap in for tests, or for a runtime with its own instrumented fetch. */
  readonly fetch?: typeof globalThis.fetch
  /**
   * A refresh token, which turns on automatic renewal. With one set, a `401`
   * from an ordinary request makes the client exchange it for a new pair once
   * and replay the request, so a caller does not see the `401`s a forced key
   * rotation or an expiry between calls would otherwise cause. Without one, a
   * `401` surfaces exactly as before — which is the right behaviour for a
   * service-account key, since `nacre_sk_` is not a JWT and has nothing to
   * refresh.
   *
   * The renewal is shared: a burst of concurrent requests that all `401`
   * triggers exactly **one** exchange, because replaying a spent refresh token
   * revokes the whole family, and a client that let ten requests each present
   * the same one would sign its user out on the second.
   */
  readonly refreshToken?: string
  /**
   * Called whenever the client rotates its tokens, with the new pair. Persist
   * the refresh token here — the old one is spent the moment the new one is
   * issued, so an application that reconstructs the client from storage without
   * this loses the session on the next start. Not called for the explicit
   * `auth.login`/`auth.refresh` methods, which hand the tokens back directly.
   */
  readonly onTokens?: (tokens: Tokens) => void
  /**
   * Called when a **renewal** is answered by a sign-in gate rather than by a
   * new pair — an organization turned on a policy while this session was live.
   *
   * Separate from `onTokens` because the two mean opposite things: one says the
   * session continues and this says it is over and why. Without it the only
   * signal is the `401` every request afterwards gets, which is
   * indistinguishable from an expired session and sends a person to a sign-in
   * screen that will hand them the same challenge again.
   */
  readonly onSignInGate?: (outcome: SecondFactorEnrolmentRequired) => void
}

/**
 * A multipart body: the fields, as strings, and one file part. Built fresh on
 * every attempt rather than once, because a `FormData` is consumed by the
 * request that sends it and a retry must not replay an empty one.
 */
interface MultipartBody {
  readonly fields: Readonly<Record<string, string>>
  readonly file: { readonly bytes: Uint8Array; readonly contentType: string; readonly filename: string }
}

interface RequestOptions {
  readonly method: string
  readonly path: string
  readonly body?: unknown
  /** Instead of `body`: the request goes up as `multipart/form-data`. */
  readonly multipart?: MultipartBody
  /** Instead of `body`: the bytes go up as they are, under their own media type. */
  readonly raw?: { readonly bytes: Uint8Array; readonly contentType: string }
  readonly signal?: AbortSignal
  /** Safe or idempotent, so a transient failure may be retried. */
  readonly retryable?: boolean
  /**
   * The answer is bytes — a skill exported as a zip — rather than JSON. A
   * refusal is still a problem document and still throws.
   */
  readonly bytes?: boolean
  /**
   * The sign-in endpoints and the internal renewal set this, so a `401` from
   * them is an answer rather than a trigger for another renewal — a refresh
   * that itself `401`s means the session is over, not that it should recurse.
   */
  readonly noAuthRefresh?: boolean
}

function formData(multipart: MultipartBody): FormData {
  const form = new FormData()
  for (const [name, value] of Object.entries(multipart.fields)) form.append(name, value)
  // A copy, so the Blob holds bytes of its own: a caller's buffer may be a
  // view over something reused before the request is sent.
  form.append(
    'file',
    new Blob([new Uint8Array(multipart.file.bytes)], { type: multipart.file.contentType }),
    multipart.file.filename,
  )
  return form
}

const DEFAULT_TIMEOUT_MS = 30_000
const DEFAULT_RETRIES = 2

const isProblem = (value: unknown): value is Problem =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { status?: unknown }).status === 'number' &&
  typeof (value as { title?: unknown }).title === 'string'

export class NacreClient {
  readonly #base: string
  #token: string
  readonly #timeoutMs: number
  readonly #retries: number
  readonly #fetch: typeof globalThis.fetch
  #refreshToken: string | undefined
  readonly #onTokens: ((tokens: Tokens) => void) | undefined
  readonly #onSignInGate: ((outcome: SecondFactorEnrolmentRequired) => void) | undefined
  /** The one in-flight renewal, shared by every request that 401s during it. */
  #renewing: Promise<boolean> | undefined

  constructor(options: ClientOptions) {
    if (!options.baseUrl) throw new TypeError('baseUrl is required')
    if (!options.token) throw new TypeError('token is required')

    // Trailing slashes are stripped so `${base}/v1/...` cannot become `//v1`,
    // which some proxies redirect and some reject.
    this.#base = options.baseUrl.replace(/\/+$/, '')
    this.#token = options.token
    this.#timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
    this.#retries = Math.max(1, options.retries ?? DEFAULT_RETRIES)
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis)
    this.#refreshToken = options.refreshToken
    this.#onTokens = options.onTokens
    this.#onSignInGate = options.onSignInGate
  }

  async #once(options: RequestOptions): Promise<unknown> {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), this.#timeoutMs)
    const onAbort = () => controller.abort()
    options.signal?.addEventListener('abort', onAbort, { once: true })

    let response: Response
    try {
      // No `content-type` for a multipart body: `fetch` writes it with the
      // boundary it chose, and a header set here would name a boundary that
      // is not the one in the body.
      response = await this.#fetch(`${this.#base}${options.path}`, {
        method: options.method,
        headers: {
          authorization: `Bearer ${this.#token}`,
          ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
          ...(options.raw === undefined ? {} : { 'content-type': options.raw.contentType }),
          accept: options.bytes === true ? 'application/zip, application/problem+json' : 'application/json',
        },
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        ...(options.multipart === undefined ? {} : { body: formData(options.multipart) }),
        // A copy, for the reason `formData` makes one: the caller's buffer may
        // be a view over something reused before the request is sent.
        ...(options.raw === undefined ? {} : { body: new Uint8Array(options.raw.bytes) }),
        signal: controller.signal,
      })
    } catch (cause) {
      throw new NacreTransportError(
        options.signal?.aborted === true
          ? 'the request was aborted by the caller'
          : `could not reach ${this.#base}`,
        undefined,
        { cause },
      )
    } finally {
      clearTimeout(timer)
      options.signal?.removeEventListener('abort', onAbort)
    }

    if (response.status === 204) return undefined
    if (options.bytes === true && response.ok) return new Uint8Array(await response.arrayBuffer())

    const text = await response.text()
    let parsed: unknown
    try {
      parsed = text.length === 0 ? undefined : JSON.parse(text)
    } catch (cause) {
      throw new NacreTransportError(
        `${options.method} ${options.path} answered ${response.status} with a body that is not JSON`,
        response.status,
        { cause },
      )
    }

    if (response.ok) return parsed

    // A problem document, or something upstream of the API answered — a proxy,
    // a load balancer, a WAF. Saying which matters: the second is not the
    // server refusing, and no request id exists to look up.
    if (isProblem(parsed)) {
      throw new NacreError(parsed, retryAfterOf(response.headers.get('retry-after')))
    }
    throw new NacreTransportError(
      `${options.method} ${options.path} answered ${response.status} without a problem document; ` +
        'something between this client and the API answered instead',
      response.status,
    )
  }

  async #attempt(options: RequestOptions): Promise<unknown> {
    let attempt = 0
    for (;;) {
      attempt++
      try {
        return await this.#once(options)
      } catch (error) {
        const transient =
          (error instanceof NacreError && error.isTransient) ||
          (error instanceof NacreTransportError && error.status === undefined)

        // Retrying a delete or a grant would be a second, different write. Only
        // the caller's own abort ends this early, and it is not transient.
        if (!options.retryable || !transient || attempt >= this.#retries) throw error
        if (options.signal?.aborted === true) throw error

        // The server's own wait where it named one, because retrying a 429
        // sooner than that is a request it has already said it will refuse —
        // and is counted against the same budget the caller is out of. A wait
        // longer than a caller would sit through without being told is not
        // slept: the error goes back, carrying `retryAfter`, for the caller to
        // decide.
        const asked = error instanceof NacreError ? error.retryAfter : undefined
        if (asked !== undefined && asked * 1000 > MAX_RETRY_WAIT_MS) throw error
        await new Promise((r) => setTimeout(r, asked !== undefined ? asked * 1000 : 250 * 2 ** (attempt - 1)))
      }
    }
  }

  /**
   * Walk a cursor-paged collection to completion, or fail loudly.
   *
   * Every `.list()` here used to fetch **one page and drop the cursor**, so a
   * console showing the result showed the first page of everything and said
   * nothing about the rest. On a grant list — "who can see this patient" — a
   * truncation that looks complete is worse than an error: it is a security
   * surface answering a narrower question than the one asked, silently.
   *
   * So this follows `next_cursor` to the end, asking for the largest page the
   * server allows (200) to keep the round trips down. The cap is the honesty:
   * past `MAX_PAGES` it throws rather than returning a partial set, because a
   * listing that quietly stops at ten thousand rows is the same lie one order
   * of magnitude up. An installation that has outgrown this reaches for the
   * filtered directory (an enterprise module) or the API's own cursors — the
   * error says so.
   */
  async #listAll<T>(path: string, map: (row: Record<string, unknown>) => T): Promise<readonly T[]> {
    const LIMIT = 200
    const MAX_PAGES = 50 // 10,000 rows; a console past this wants a filter, not a scroll
    const items: T[] = []
    let cursor: string | undefined
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const query = new URLSearchParams({ limit: String(LIMIT) })
      if (cursor !== undefined) query.set('cursor', cursor)
      const body = (await this.#request({
        method: 'GET',
        path: `${path}?${query.toString()}`,
        retryable: true,
      })) as { items?: unknown[]; next_cursor?: string | null }
      for (const row of body.items ?? []) items.push(map(row as Record<string, unknown>))
      const next = body.next_cursor
      if (next === null || next === undefined || next === '') return items
      cursor = next
    }
    throw new Error(
      `This organization has more than ${String(LIMIT * MAX_PAGES)} rows under ${path}. ` +
        'The console lists what fits; filter with the directory (an enterprise module) ' +
        'or page the API directly with ?cursor.',
    )
  }

  async #request(options: RequestOptions): Promise<unknown> {
    try {
      return await this.#attempt(options)
    } catch (error) {
      // A 401 on an ordinary request, with a refresh token in hand: renew once
      // and replay. `noAuthRefresh` keeps the sign-in endpoints and the renewal
      // itself out of this, so a spent refresh token ends the session instead
      // of recursing. The replay is not wrapped — a second 401 is the caller's
      // answer, not a reason to renew again.
      if (
        this.#refreshToken !== undefined &&
        options.noAuthRefresh !== true &&
        error instanceof NacreError &&
        error.status === 401 &&
        (await this.#renewOnce())
      ) {
        return await this.#attempt(options)
      }
      throw error
    }
  }

  /**
   * Exchange the refresh token for a new pair, at most once concurrently.
   *
   * Every request that 401s during a renewal awaits the same promise rather
   * than starting its own — a spent refresh token revokes the whole family, so
   * a burst presenting it in parallel would sign the user out. Returns whether
   * the client now holds a fresh access token.
   */
  #renewOnce(): Promise<boolean> {
    this.#renewing ??= this.#renew().finally(() => {
      this.#renewing = undefined
    })
    return this.#renewing
  }

  async #renew(): Promise<boolean> {
    const refreshToken = this.#refreshToken
    if (refreshToken === undefined) return false

    let renewed: Tokens | SecondFactorEnrolmentRequired | undefined
    try {
      renewed = await this.auth.refresh(refreshToken)
    } catch {
      // A transport error says nothing about the token's validity, so keep it
      // and let a later request try again rather than ending the session on a
      // blip.
      return false
    }

    // `undefined` is the server refusing the refresh token: the session is over
    // and re-presenting it only 401s again, so drop it and let the 401 surface.
    if (renewed === undefined) {
      this.#refreshToken = undefined
      return false
    }

    /*
     * A gate answered instead of renewing, and this is the case that would have
     * been silent.
     *
     * The body carries no `access_token`, so a renewal that assumed tokens
     * would store the string `"undefined"` and every request afterwards would
     * 401 with nothing anywhere saying why. The refresh token is spent by the
     * server either way, so the session is over: drop it, tell whoever is
     * listening, and let the 401 surface to the caller.
     */
    if ('secondFactorEnrolmentRequired' in renewed) {
      this.#refreshToken = undefined
      this.#onSignInGate?.(renewed)
      return false
    }

    this.#token = renewed.accessToken
    this.#refreshToken = renewed.refreshToken
    this.#onTokens?.(renewed)
    return true
  }

  /**
   * `undefined` for a 401 — the sign-in paths, where a refusal is an answer.
   *
   * Separate from `#maybe` because the two mean different things and must not
   * be merged: a 404 elsewhere is "absent or invisible", and a 401 here is
   * "these credentials are not valid", which the server deliberately does not
   * elaborate on. Both are answers rather than faults, and neither is the
   * other.
   */
  async #unauthorized<T>(options: RequestOptions): Promise<T | undefined> {
    try {
      return (await this.#request(options)) as T
    } catch (error) {
      if (error instanceof NacreError && error.status === 401) return undefined
      throw error
    }
  }

  /** `undefined` for 404 — absent and invisible are one answer. */
  async #maybe<T>(options: RequestOptions): Promise<T | undefined> {
    try {
      return (await this.#request(options)) as T
    } catch (error) {
      if (error instanceof NacreError && error.isNotFound) return undefined
      throw error
    }
  }

  // ─── search ──────────────────────────────────────────────────────────────

  /**
   * Search, bounded by what this token may see.
   *
   * `topK` goes through uncorrected: the filter is applied inside the index
   * traversal, so this many permitted results come back rather than this many
   * candidates minus whatever was stripped afterwards.
   */
  async search(query: string, options: SearchOptions = {}): Promise<readonly SearchHit[]> {
    const body = await this.#request({
      method: 'POST',
      path: '/v1/search',
      body: {
        query,
        top_k: options.topK ?? 10,
        ...(options.layers === undefined ? {} : { layers: options.layers }),
        ...(options.filters === undefined ? {} : { filters: options.filters }),
        ...(options.includeContent === undefined ? {} : { include_content: options.includeContent }),
        ...(options.rerank === undefined ? {} : { rerank: options.rerank }),
      },
      ...(options.signal === undefined ? {} : { signal: options.signal }),
      retryable: true,
    })

    const items = (body as { items?: unknown[] }).items ?? []
    return items.map((h) => {
      const hit = h as Record<string, unknown>
      return {
        // `doc_id`, which is what the contract calls it and what the server
        // sends. This read `document_id` — a field that exists in the contract,
        // on `Job`, and not on a search hit — so `?? ''` turned every hit's
        // document id into an empty string and nothing failed. A search result
        // could not be turned into `documents.get(id)`, which is the whole
        // find-it-then-fetch-it flow, through the SDK and therefore through the
        // admin UI as well.
        documentId: String(hit.doc_id ?? ''),
        chunkId: String(hit.chunk_id ?? ''),
        score: Number(hit.score ?? 0),
        text: String(hit.text ?? ''),
        layer: String(hit.layer ?? ''),
        title: (hit.title as string | null) ?? null,
      }
    })
  }

  // ─── documents ───────────────────────────────────────────────────────────

  readonly documents = {
    /**
     * Queue a document. `202` and a job id, or `200` and `unchanged: true` when
     * the same bytes are already indexed.
     */
    add: async (request: IngestRequest): Promise<IngestOutcome> => {
      // Exactly one source, refused here rather than by the server: the three
      // travel in two different envelopes, so a request carrying two would
      // otherwise be sent in whichever one this method picked and refused for
      // a reason about the wrong field.
      const sources = [request.content, request.url, request.bytes].filter((s) => s !== undefined)
      if (sources.length !== 1) {
        throw new TypeError('exactly one of content, url or bytes is required')
      }
      if (request.bytes !== undefined && request.contentType === undefined) {
        throw new TypeError('bytes need a contentType: the server refuses to sniff a binary file')
      }

      // A binary document goes up as the multipart form the contract
      // describes — the bytes under their real type on the file part, every
      // other field as a string, `metadata` as JSON text. Text and URL keep
      // the JSON body they always had.
      const options =
        request.bytes === undefined
          ? {
              body: {
                layer: request.layer,
                external_id: request.externalId,
                ...(request.title === undefined ? {} : { title: request.title }),
                ...(request.content === undefined ? {} : { content: request.content }),
                ...(request.url === undefined ? {} : { url: request.url }),
                ...(request.metadata === undefined ? {} : { metadata: request.metadata }),
              },
            }
          : {
              multipart: {
                fields: {
                  layer: request.layer,
                  external_id: request.externalId,
                  ...(request.title === undefined ? {} : { title: request.title }),
                  ...(request.metadata === undefined ? {} : { metadata: JSON.stringify(request.metadata) }),
                },
                file: {
                  bytes: request.bytes,
                  contentType: request.contentType as string,
                  filename: request.filename ?? request.externalId,
                },
              },
            }

      const body = (await this.#request({
        method: 'POST',
        path: '/v1/documents',
        ...options,
        // Idempotent on (layer, external_id) and the content hash, so a retry
        // after a timeout cannot produce a second document or a second version.
        retryable: true,
      })) as Record<string, unknown>

      return {
        documentId: String(body.document_id),
        jobId: String(body.job_id),
        unchanged: body.status === 'indexed',
      }
    },

    get: async (documentId: string): Promise<Document | undefined> => {
      const body = await this.#maybe<Record<string, unknown>>({
        method: 'GET',
        path: `/v1/documents/${encodeURIComponent(documentId)}`,
        retryable: true,
      })
      if (body === undefined) return undefined

      return {
        documentId: String(body.document_id),
        externalId: (body.external_id as string | null) ?? null,
        layer: String(body.layer ?? ''),
        title: (body.title as string | null) ?? null,
        status: body.status as Document['status'],
        chunkCount: Number(body.chunk_count ?? 0),
        updatedAt: String(body.updated_at ?? ''),
      }
    },

    /**
     * Replace a document's metadata without re-indexing it.
     *
     * `false` for a document that is absent, another organization's, or one
     * this credential may not write to — one answer for all three, which is
     * what keeps "no such document" and "not yours" indistinguishable.
     *
     * A replacement, not a merge: send every tag the document should have.
     */
    setMetadata: async (
      documentId: string,
      metadata: Readonly<Record<string, string | number | boolean | readonly (string | number | boolean)[]>>,
    ): Promise<boolean> => {
      try {
        await this.#request({
          method: 'PATCH',
          path: `/v1/documents/${encodeURIComponent(documentId)}`,
          body: { metadata },
          // Replacing a value with the same value is the same value. A retry
          // after a timeout cannot produce a second anything.
          retryable: true,
        })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return false
        throw error
      }
    },

    /**
     * Put a failed document back in the queue, without re-sending its content.
     *
     * `'requeued'` when it is queued again; `'not-failed'` when this caller may
     * write the document and it has nothing to retry; `'unreachable'` when it
     * is absent, another organization's, or one this credential may not write.
     *
     * Three values rather than a boolean, and the two refusals are matched on
     * the problem **type** rather than on the status: they are different
     * statements to whoever reads them, and collapsing them sends somebody
     * looking for a document that is on their screen.
     *
     * The worker retries a transient failure by itself. This is for the class
     * that will not come back on its own — too long, unreadable, over a quota,
     * or out of attempts — once whatever caused it has been changed.
     */
    retry: async (documentId: string): Promise<'requeued' | 'not-failed' | 'unreachable'> => {
      try {
        await this.#request({
          method: 'POST',
          path: `/v1/documents/${encodeURIComponent(documentId)}/retry`,
          // Requeueing a document that is already queued is the same queue. A
          // retry after a timeout cannot produce a second anything.
          retryable: true,
        })
        return 'requeued'
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return 'unreachable'
        if (error instanceof NacreError && error.type === 'https://nacre.work/errors/conflict') {
          return 'not-failed'
        }
        throw error
      }
    },

    /**
     * Delete a document. `true` when it was deleted, `false` when it was
     * already gone or was never visible to this caller.
     *
     * It leaves search immediately — the vectors are flagged before the row is
     * written, and collection of the points is a background job that nothing
     * depends on.
     */
    remove: async (documentId: string): Promise<boolean> => {
      try {
        await this.#request({
          method: 'DELETE',
          path: `/v1/documents/${encodeURIComponent(documentId)}`,
        })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return false
        throw error
      }
    },
  }

  // ─── uploads ─────────────────────────────────────────────────────────────

  /**
   * A file sent to the index by whoever holds the bytes, on a ticket minted
   * by whoever holds `write`. The two halves are two methods because they
   * run in two places: an agent mints a ticket and hands the descriptor to a
   * shell, a page or a script, which sends the bytes with no credential at
   * all. `documents.add` with `bytes` is the one-process form.
   */
  readonly uploads = {
    /** Mint a ticket: single-use, five minutes, for a layer the caller may write to. */
    create: async (request: UploadTicketRequest): Promise<UploadDescriptor> => {
      const body = (await this.#request({
        method: 'POST',
        path: '/v1/uploads',
        body: {
          layer: request.layer,
          ...(request.externalId === undefined ? {} : { external_id: request.externalId }),
          ...(request.title === undefined ? {} : { title: request.title }),
          ...(request.metadata === undefined ? {} : { metadata: request.metadata }),
        },
        // Minting is not idempotent — every attempt that reached the server
        // is a ticket — but an unredeemed ticket costs nothing and expires,
        // so a retry after a timeout buys a working descriptor rather than a
        // stray document.
        retryable: true,
      })) as Record<string, unknown>
      return {
        ticket: String(body.ticket),
        url: String(body.url),
        method: 'POST',
        headers: (body.headers as Record<string, string> | undefined) ?? {},
        expiresAt: String(body.expires_at),
        maxSize: Number(body.max_size),
        accepts: (body.accepts as string[] | undefined) ?? [],
        curl: String(body.curl),
      }
    },

    /**
     * Redeem a ticket with the file as the body, under its own media type.
     *
     * Deliberately not retried: the ticket is spent by the first request that
     * reaches the server, so a retry after a timeout that did arrive answers
     * `404` — and a `404` here means mint again, never that the document is
     * missing. `filename` names the document when the ticket fixed no
     * `externalId`.
     */
    send: async (
      ticket: string,
      bytes: Uint8Array,
      contentType: string,
      filename?: string,
    ): Promise<IngestOutcome> => {
      const query = filename === undefined ? '' : `?filename=${encodeURIComponent(filename)}`
      const body = (await this.#request({
        method: 'POST',
        path: `/v1/uploads/${encodeURIComponent(ticket)}${query}`,
        raw: { bytes, contentType },
        retryable: false,
      })) as Record<string, unknown>
      return {
        documentId: String(body.document_id),
        jobId: String(body.job_id),
        unchanged: body.status === 'indexed',
      }
    },
  }

  // ─── jobs ────────────────────────────────────────────────────────────────

  readonly jobs = {
    get: async (jobId: string): Promise<Job | undefined> => {
      const body = await this.#maybe<Record<string, unknown>>({
        method: 'GET',
        path: `/v1/jobs/${encodeURIComponent(jobId)}`,
        retryable: true,
      })
      if (body === undefined) return undefined

      return {
        jobId: String(body.job_id),
        documentId: String(body.document_id),
        status: body.status as Job['status'],
        error: (body.error as string | null) ?? null,
      }
    },

    /**
     * Poll until the job leaves the queue.
     *
     * Ingest is asynchronous by design — parsing and embedding are slow enough
     * that holding a request open for them is how a client times out and
     * retries into a second copy of the same work. This is the wait, written
     * once, so every caller does not write it slightly differently.
     *
     * Resolves on `indexed` **and** on `failed`: both are terminal, and a
     * helper that threw on failure would bury the reason the job carries.
     */
    wait: async (
      jobId: string,
      options: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal } = {},
    ): Promise<Job | undefined> => {
      const interval = options.intervalMs ?? 1000
      const deadline = Date.now() + (options.timeoutMs ?? 300_000)

      for (;;) {
        const job = await this.jobs.get(jobId)
        if (job === undefined) return undefined
        if (job.status === 'indexed' || job.status === 'failed') return job

        if (Date.now() + interval > deadline) {
          throw new NacreTransportError(`job ${jobId} was still ${job.status} after the timeout`)
        }
        if (options.signal?.aborted === true) {
          throw new NacreTransportError('the wait was aborted by the caller')
        }
        await new Promise((r) => setTimeout(r, interval))
      }
    },
  }

  // ─── layers ──────────────────────────────────────────────────────────────

  /**
   * Who this token belongs to.
   *
   * The one call a client can make before it knows anything: it composes its
   * answer from the presented token and names no other principal. A UI needs it
   * to know which controls to offer — without it the admin UI drew every button
   * for everybody, and a member pressing one got the `404` invariant 4 requires,
   * which reads as a broken application rather than as a permission they lack.
   */
  readonly me = async (): Promise<Self> => {
    const body = (await this.#request({ method: 'GET', path: '/v1/me', retryable: true })) as Record<string, unknown>
    return {
      organization: String(body.organization),
      principalType: body.principal_type === 'service_account' ? 'service_account' : 'user',
      principalId: String(body.principal_id),
      role: String(body.role) as Self['role'],
      // Falls back to the *conservative* half of the derivation this field
      // replaces, and only that half: an older API that does not report it
      // leaves an `org_admin` administering and a `platform_admin` not, which
      // is what the server would have said anyway. The other direction — the
      // one the console shipped — is what offered three screens that answer
      // 404.
      administers:
        typeof body.administers === 'boolean' ? body.administers : body.role === 'org_admin',
      // Defaulted to true for an older API that does not answer it, which is
      // the right way round: a screen that hides its password control against a
      // server that would have accepted the change has taken a working feature
      // away, and a screen that shows one against a server that refuses gets a
      // 404 the person can read.
      holdsOwnCredentials: body.holds_own_credentials !== false,
      managesEmbedders: body.manages_embedders === true,
    }
  }

  /**
   * Where a client connects to this installation: the REST base, the MCP
   * endpoint, and the administrative one for somebody who administers the
   * organization.
   *
   * Asked rather than derived from wherever this client happens to be
   * pointed: the API's base is not the MCP transport's address on every
   * deployment, and the server already knows which addresses are the public
   * ones.
   */
  readonly endpoints = async (): Promise<Endpoints> => {
    const body = (await this.#request({ method: 'GET', path: '/v1/endpoints', retryable: true })) as Record<string, unknown>
    return {
      api: String(body.api),
      mcp: String(body.mcp),
      ...(typeof body.mcp_admin === 'string' ? { mcpAdmin: body.mcp_admin } : {}),
      contract: String(body.contract),
      version: String(body.version),
    }
  }

  /**
   * Workspaces. A layer needs one, and until this endpoint existed the only
   * way to have its id was the line `init` printed.
   */
  readonly workspaces = {
    /** Only the workspaces this token can reach. The catalog is permission data. */
    list: async (): Promise<readonly Workspace[]> =>
      this.#listAll('/v1/workspaces', (ws) => ({
        id: String(ws.id),
        slug: String(ws.slug),
        name: String(ws.name ?? ''),
        layerCount: Number(ws.layer_count ?? 0),
        permissions: readPermissions(ws.permissions),
      })),

    /** `org_admin` only — there is no scope above a workspace to hold a grant on. */
    create: async (input: { slug: string; name: string }): Promise<Workspace | undefined> => {
      const body = await this.#maybe<Record<string, unknown>>({
        method: 'POST',
        path: '/v1/workspaces',
        body: { slug: input.slug, name: input.name },
      })
      if (body === undefined) return undefined

      return {
        id: String(body.id),
        slug: String(body.slug),
        name: String(body.name ?? ''),
        layerCount: Number(body.layer_count ?? 0),
        permissions: readPermissions(body.permissions),
      }
    },
  }

  /**
   * The embedding models a layer can be pointed at.
   *
   * Listing is open to anybody in the organization, because the caller who
   * needs it is whoever may start a reindex — `admin` on the layer, not an
   * organization role. Creating one is `org_admin`.
   */
  readonly embeddingProviders = {
    list: async (): Promise<readonly EmbeddingProvider[]> => {
      const body = (await this.#request({
        method: 'GET',
        path: '/v1/embedding-providers',
        retryable: true,
      })) as { items?: unknown[] }
      return (body.items ?? []).map((p) => providerFrom(p as Record<string, unknown>))
    },

    /** `undefined` for a caller who is not an `org_admin` — the usual 404. */
    create: async (input: {
      name: string
      endpoint: string
      model: string
      dimensions: number
    }): Promise<EmbeddingProvider | undefined> => {
      const body = await this.#maybe<Record<string, unknown>>({
        method: 'POST',
        path: '/v1/embedding-providers',
        body: {
          name: input.name,
          endpoint: input.endpoint,
          model: input.model,
          dimensions: input.dimensions,
        },
      })
      return body === undefined ? undefined : providerFrom(body)
    },

    /**
     * Remove one this organization owns.
     *
     * `'removed'` on success; `'in-use'` when a layer still names it (move or
     * delete those first); `'unreachable'` when it is absent, another
     * organization's (the global default included), or this caller may not
     * manage providers. Matched on the problem **type**, because a `409` and a
     * `404` are two facts a status is too coarse to carry.
     */
    remove: async (id: string): Promise<'removed' | 'in-use' | 'unreachable'> => {
      try {
        await this.#request({
          method: 'DELETE',
          path: `/v1/embedding-providers/${encodeURIComponent(id)}`,
        })
        return 'removed'
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return 'unreachable'
        if (error instanceof NacreError && error.type === 'https://nacre.work/errors/conflict') {
          return 'in-use'
        }
        throw error
      }
    },
  }

  readonly layers = {
    /** Only the layers this token may read. The catalog is permission data. */
    list: async (): Promise<readonly Layer[]> =>
      this.#listAll('/v1/layers', (layer) => ({
        id: String(layer.id),
        slug: String(layer.slug),
        name: String(layer.name ?? ''),
        description: String(layer.description ?? ''),
        workspaceId: String(layer.workspace_id ?? ''),
        documentCount: Number(layer.document_count ?? 0),
        failedCount: Number(layer.failed_count ?? 0),
        permissions: readPermissions(layer.permissions),
      })),

    create: async (input: LayerInput): Promise<Layer | undefined> => {
      const body = await this.#maybe<Record<string, unknown>>({
        method: 'POST',
        path: '/v1/layers',
        body: {
          workspace_id: input.workspaceId,
          slug: input.slug,
          name: input.name,
          ...(input.description === undefined ? {} : { description: input.description }),
          ...(input.providerId === undefined ? {} : { provider_id: input.providerId }),
        },
      })
      if (body === undefined) return undefined

      return {
        id: String(body.id),
        slug: String(body.slug),
        name: String(body.name ?? ''),
        description: String(body.description ?? ''),
        workspaceId: String(body.workspace_id ?? input.workspaceId),
        documentCount: Number(body.document_count ?? 0),
        failedCount: Number(body.failed_count ?? 0),
        permissions: readPermissions(body.permissions),
      }
    },

    /**
     * Rename a layer, or change its description.
     *
     * Name and description only. What model built the vectors is not editable
     * here and is not an oversight: changing it is a reindex, because the
     * vectors have to be rebuilt, and a `PATCH` that quietly started one would
     * be an expensive background job triggered by an edit form.
     *
     * `false` for a layer this token may not administer and for one that is not
     * there — the usual rule, and the same answer.
     */
    update: async (
      layerId: string,
      changes: { name?: string; description?: string },
    ): Promise<boolean> => {
      try {
        await this.#request({
          method: 'PATCH',
          path: `/v1/layers/${encodeURIComponent(layerId)}`,
          body: {
            ...(changes.name === undefined ? {} : { name: changes.name }),
            ...(changes.description === undefined ? {} : { description: changes.description }),
          },
        })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return false
        throw error
      }
    },

    /**
     * Delete a layer and everything in it.
     *
     * Returns as soon as the layer stops resolving and its documents stop
     * matching — the points and any stored bytes are reclaimed by the
     * collector afterwards, and nothing depends on when. `false` for absent,
     * for another organization's, and for one this token may not administer.
     */
    remove: async (layerId: string): Promise<boolean> => {
      try {
        await this.#request({
          method: 'DELETE',
          path: `/v1/layers/${encodeURIComponent(layerId)}`,
        })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return false
        throw error
      }
    },

    /**
     * Move the layer onto a different embedding model.
     *
     * Returns immediately with the state to poll; the work happens in the
     * worker and search keeps answering from the old vectors throughout. There
     * is no cancel — starting a reindex back onto the previous provider is how
     * one is undone.
     *
     * Throws `NacreError` with status 409 when one is already running on this
     * layer, and 400 for a provider that does not exist or that names the model
     * the layer already uses.
     */
    reindex: async (layerId: string, providerId: string): Promise<ReindexStatus | undefined> => {
      const body = await this.#maybe<Record<string, unknown>>({
        method: 'POST',
        path: `/v1/layers/${encodeURIComponent(layerId)}/reindex`,
        body: { provider_id: providerId },
      })
      return body === undefined ? undefined : reindexFrom(body)
    },

    /** How far a reindex has got. `undefined` when none has ever run. */
    reindexStatus: async (layerId: string): Promise<ReindexStatus | undefined> => {
      const body = await this.#maybe<Record<string, unknown>>({
        method: 'GET',
        path: `/v1/layers/${encodeURIComponent(layerId)}/reindex`,
        retryable: true,
      })
      return body === undefined ? undefined : reindexFrom(body)
    },

    /**
     * The query set a reindex of this layer is checked against.
     *
     * A migration onto a new model succeeds mechanically whether or not the new
     * model works, so this is what stands between "every document got a vector"
     * and "search still answers". Empty — the default — means the layer has no
     * gate, because the check needs documents somebody chose.
     *
     * `admin` on the layer, not `read`: the entries name the documents you
     * consider the canonical answers to a query.
     */
    referenceQueries: async (layerId: string): Promise<readonly ReferenceQuery[] | undefined> => {
      const body = await this.#maybe<{ items?: unknown[] }>({
        method: 'GET',
        path: `/v1/layers/${encodeURIComponent(layerId)}/reference-queries`,
        retryable: true,
      })
      return body === undefined ? undefined : (body.items ?? []).map(referenceQueryFrom)
    },

    /**
     * Replace that set whole. `[]` removes the gate.
     *
     * Replaced rather than edited entry by entry, which is the server's shape
     * too: a reference set is one statement about what search must keep doing,
     * and a partial edit is how half of one ends up describing a layer nobody
     * has looked at since.
     *
     * At most 50 queries, each naming at most 10 documents — that second bound
     * is how many the check retrieves, so a longer list could never score 1.0.
     * The external ids need not exist yet; a set is often written before the
     * documents it names are ingested.
     */
    setReferenceQueries: async (
      layerId: string,
      queries: readonly ReferenceQueryInput[],
    ): Promise<readonly ReferenceQuery[] | undefined> => {
      const body = await this.#maybe<{ items?: unknown[] }>({
        method: 'PUT',
        path: `/v1/layers/${encodeURIComponent(layerId)}/reference-queries`,
        body: { queries: queries.map((q) => ({ query: q.query, expected: [...q.expected] })) },
      })
      return body === undefined ? undefined : (body.items ?? []).map(referenceQueryFrom)
    },
  }

  // ─── skills ──────────────────────────────────────────────────────────────

  /**
   * What an agent is told about this installation, this organization and a
   * layer — Claude's skill format, at three levels. docs/skills.md.
   *
   * Every write names the version it starts from and a stale one comes back as
   * `{ kind: 'conflict', current }` rather than as an error, because the next
   * step is ordinary: read, merge, write again.
   */
  readonly skills = {
    /** The base skill and every layer skill this token sees, walked to the end. */
    list: async (): Promise<{ readonly base: SkillEntry; readonly layers: readonly SkillEntry[] }> => {
      const LIMIT = 200
      const MAX_PAGES = 50
      const layers: SkillEntry[] = []
      let base: SkillEntry | undefined
      let cursor: string | undefined
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const params = new URLSearchParams({ limit: String(LIMIT) })
        if (cursor !== undefined) params.set('cursor', cursor)
        const query = `?${params.toString()}`
        const body = (await this.#request({
          method: 'GET',
          path: `/v1/skills${query}`,
          retryable: true,
        })) as { base?: unknown; items?: unknown[]; next_cursor?: string | null }
        base ??= skillEntryFrom(body.base as Record<string, unknown>)
        for (const row of body.items ?? []) layers.push(skillEntryFrom(row as Record<string, unknown>))
        if (body.next_cursor === null || body.next_cursor === undefined || body.next_cursor === '') {
          return { base, layers }
        }
        cursor = body.next_cursor
      }
      throw new Error(
        `More than ${String(LIMIT * MAX_PAGES)} layer skills are visible to this token; page /v1/skills with ?cursor.`,
      )
    },

    /** The skill this token's agents are given, with its files. */
    base: async (): Promise<BaseSkill> => {
      const body = (await this.#request({ method: 'GET', path: '/v1/skills/base', retryable: true })) as Record<
        string,
        unknown
      >
      return { ...skillEntryFrom(body), files: (body.files ?? {}) as SkillFiles }
    },

    /** The base skill as a zip Claude installs. */
    exportBase: async (): Promise<Uint8Array> =>
      (await this.#request({ method: 'GET', path: '/v1/skills/base/export', retryable: true, bytes: true })) as Uint8Array,

    /** The current version with its files, or `undefined` where none is set or visible. */
    get: async (level: SkillLevel): Promise<SkillVersion | undefined> => {
      const body = await this.#maybe<Record<string, unknown>>({
        method: 'GET',
        path: `/v1/skills/${skillSegment(level)}`,
        retryable: true,
      })
      return body === undefined ? undefined : skillVersionFrom(body)
    },

    /**
     * Write a new version — a folder as `files`, or a `.zip` as Claude exports
     * one. `basedOn` is the version this starts from, 0 for a level never
     * written. `undefined` where the level is not there or not visible.
     */
    write: async (
      level: SkillLevel,
      skill: { readonly files: SkillFiles } | { readonly zip: Uint8Array },
      basedOn: number,
    ): Promise<SkillWrite | undefined> =>
      this.#skillWrite(() =>
        'zip' in skill
          ? this.#request({
              method: 'PUT',
              path: `/v1/skills/${skillSegment(level)}${basedOnQuery(basedOn)}`,
              raw: { bytes: skill.zip, contentType: 'application/zip' },
            })
          : this.#request({
              method: 'PUT',
              path: `/v1/skills/${skillSegment(level)}`,
              body: { files: skill.files, based_on: basedOn },
            }),
      ),

    /** Clear it, so the level above applies. A version too. */
    clear: async (level: SkillLevel, basedOn: number): Promise<SkillWrite | undefined> =>
      this.#skillWrite(() =>
        this.#request({ method: 'DELETE', path: `/v1/skills/${skillSegment(level)}${basedOnQuery(basedOn)}` }),
      ),

    /** History, newest first, walked to the end. `undefined` unless this token may write the level. */
    versions: async (level: SkillLevel): Promise<readonly SkillVersion[] | undefined> => {
      try {
        return await this.#listAll(`/v1/skills/${skillSegment(level)}/versions`, skillVersionFrom)
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return undefined
        throw error
      }
    },

    /** One version, with its files. */
    version: async (level: SkillLevel, version: number): Promise<SkillVersion | undefined> => {
      const body = await this.#maybe<Record<string, unknown>>({
        method: 'GET',
        path: `/v1/skills/${skillSegment(level)}/versions/${String(version)}`,
        retryable: true,
      })
      return body === undefined ? undefined : skillVersionFrom(body)
    },

    /** Roll back to `version`, as a new version. */
    restore: async (level: SkillLevel, version: number, basedOn: number): Promise<SkillWrite | undefined> =>
      this.#skillWrite(() =>
        this.#request({
          method: 'POST',
          path: `/v1/skills/${skillSegment(level)}/versions/${String(version)}/restore`,
          body: { based_on: basedOn },
        }),
      ),

    /** The current version as a zip Claude installs, or `undefined`. */
    export: async (level: SkillLevel): Promise<Uint8Array | undefined> =>
      this.#maybe<Uint8Array>({
        method: 'GET',
        path: `/v1/skills/${skillSegment(level)}/export`,
        retryable: true,
        bytes: true,
      }),
  }

  /** A write's three answers: written, a conflict to merge, or not there. */
  async #skillWrite(send: () => Promise<unknown>): Promise<SkillWrite | undefined> {
    try {
      const body = (await send()) as Record<string, unknown>
      return { kind: 'written', version: skillVersionFrom(body), cleared: body.cleared === true || body.name === null }
    } catch (error) {
      if (error instanceof NacreError && error.isNotFound) return undefined
      if (error instanceof NacreError && error.status === 409 && error.type.endsWith('/skill-version-conflict')) {
        return { kind: 'conflict', current: currentVersionOf(error) }
      }
      throw error
    }
  }

  // ─── grants ──────────────────────────────────────────────────────────────

  readonly grants = {
    // Walked to the end on purpose: "who can see this patient" answered from
    // the first page of grants is a security question answered wrong.
    list: async (): Promise<readonly Grant[]> => this.#listAll('/v1/grants', grantFrom),

    /**
     * Issue a grant. Requires admin on the scope being granted, not admin in
     * general — otherwise holding admin on one layer would be a way to grant
     * yourself another.
     */
    issue: async (input: GrantInput): Promise<Grant | undefined> => {
      const body = await this.#maybe<Record<string, unknown>>({
        method: 'POST',
        path: '/v1/grants',
        body: {
          principal_type: input.principalType,
          principal_id: input.principalId,
          scope_type: input.scopeType,
          scope_id: input.scopeId,
          permission: input.permission,
        },
      })
      return body === undefined ? undefined : grantFrom(body)
    },

    /**
     * Withdraw a grant. `false` when it is absent or on a scope this caller may
     * not administer.
     *
     * The change is reflected on the next request. The permitted set is computed
     * per request from the grants, so a revoked grant is gone from results
     * immediately — there is nothing asynchronous to propagate and nothing to
     * wait on.
     */
    revoke: async (grantId: string): Promise<boolean> => {
      try {
        await this.#request({
          method: 'DELETE',
          path: `/v1/grants/${encodeURIComponent(grantId)}`,
        })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return false
        throw error
      }
    },
  }

  // ─── service accounts ────────────────────────────────────────────────────

  /**
   * Approve an OAuth request: as an agent, or as yourself.
   *
   * The one call in the flow that creates authority, and the reason it lives on
   * an authenticated client. Naming a `serviceAccountId` gives the client that
   * **agent's** reach, which is its own principal with its own grants — the
   * token that comes back never acts as the person who approved it. Naming none
   * is a **delegation**: the client acts as the person, reaching exactly what
   * they reach and nothing more, re-resolved on every request.
   *
   * `layers` narrows a delegation to chosen layers, by id. It can only ever
   * remove, and it is meaningless beside an agent — an agent's reach is its
   * grants, which are an administrator's to set.
   *
   * Returns where the browser should go — the redirect is the page's to perform,
   * not the API's, because this is an XHR from a screen the person is looking
   * at.
   */
  readonly consent = async (input: {
    clientId: string
    redirectUri: string
    codeChallenge: string
    /** Absent means the client acts as the signed-in person. */
    serviceAccountId?: string
    /**
     * Layers a delegation is restricted to. Empty means no restriction.
     *
     * A bare id inherits the connection's ceiling — which is what every entry
     * meant before a layer could carry one — and an object sets a ceiling for
     * that layer alone. The per-layer set is intersected with `permissions`
     * and never replaces it; one naming a permission the connection excludes
     * is refused with a `400` rather than stored as a control that does
     * nothing.
     */
    layers?: readonly (string | { id: string; permissions?: readonly CeilingValue[] })[]
    /**
     * Permissions a delegation may exercise. Omit for no ceiling.
     *
     * A set rather than a level: `['write']` alone is an ingest client that
     * cannot read back what it wrote, which is rule 6 and is deliberately
     * expressible. Empty is refused — that would be a delegation that can do
     * nothing, which is not what omitting a restriction means.
     *
     * `skill` lets the application write a layer's skill where the person
     * holds `admin`, and confers nothing else; per layer it is the consent
     * screen's "edit this layer's skill" box.
     */
    permissions?: readonly CeilingValue[]
    state?: string
    /**
     * The RFC 8707 resource indicator the client sent. One naming `…/mcp/admin`
     * makes this the administrative consent: an organization administrator
     * only, as the signed-in person, with no `layers` and no `permissions` —
     * the server fixes what that connection may do.
     */
    resource?: string
  }): Promise<string> => {
    const body = (await this.#request({
      method: 'POST',
      path: '/v1/oauth/consent',
      body: {
        client_id: input.clientId,
        redirect_uri: input.redirectUri,
        code_challenge: input.codeChallenge,
        ...(input.serviceAccountId === undefined ? {} : { service_account_id: input.serviceAccountId }),
        ...(input.layers === undefined || input.layers.length === 0
          ? {}
          : {
              layers: input.layers.map((l) =>
                typeof l === 'string'
                  ? l
                  : { id: l.id, ...(l.permissions === undefined ? {} : { permissions: [...l.permissions] }) },
              ),
            }),
        ...(input.permissions === undefined ? {} : { permissions: [...input.permissions] }),
        ...(input.state === undefined ? {} : { state: input.state }),
        ...(input.resource === undefined ? {} : { resource: input.resource }),
      },
    })) as { redirect_to?: unknown }
    return String(body.redirect_to)
  }

  /**
   * Decline an application's request: the address to send the browser to,
   * which is the client's registered redirect URI carrying
   * `error=access_denied` and its `state`. The server checks the address
   * against the registration, as it does for an approval, so a consent link
   * somebody wrote by hand cannot make Cancel go anywhere else.
   */
  declineConsent = async (input: {
    clientId: string
    redirectUri: string
    codeChallenge: string
    state?: string
  }): Promise<string> => {
    const body = (await this.#request({
      method: 'POST',
      path: '/v1/oauth/consent',
      body: {
        decision: 'deny',
        client_id: input.clientId,
        redirect_uri: input.redirectUri,
        code_challenge: input.codeChallenge,
        ...(input.state === undefined ? {} : { state: input.state }),
      },
    })) as { redirect_to?: unknown }
    return String(body.redirect_to)
  }

  /**
   * Applications connected to this organization, and ending one.
   *
   * "Forget this application" is what a person actually wants when they are
   * done with a client, and it is a different act from revoking the agent: an
   * agent may have several connections and a key of its own in use elsewhere.
   */
  readonly connections = {
    list: async (): Promise<{ items: readonly Connection[]; accessTokenTtlSeconds: number }> => {
      const body = (await this.#request({
        method: 'GET',
        path: '/v1/oauth/consents',
        retryable: true,
      })) as { items?: unknown[]; access_token_ttl_seconds?: unknown }
      return {
        items: (body.items ?? []).map((raw) => {
          const c = raw as Record<string, unknown>
          return {
            id: String(c.id),
            clientId: String(c.client_id),
            clientName: String(c.client_name),
            // A delegation names no agent. `null` rather than the string
            // "null", which is what `String(...)` produced and what would have
            // rendered on the connections screen.
            actsAs: c.acts_as === 'user' ? ('user' as const) : ('service_account' as const),
            serviceAccountId: c.service_account_id == null ? null : String(c.service_account_id),
            serviceAccountName: c.service_account_name == null ? null : String(c.service_account_name),
            approvedBy: String(c.approved_by),
            // Null rather than the string "null", which is the defect the two
            // lines above already record — a `String(...)` over an absent field
            // renders on the screen.
            approvedByEmail: c.approved_by_email == null ? null : String(c.approved_by_email),
            approverDisabled: c.approver_disabled === true,
            // Objects, `{ id, permissions? }`. This was `map(String)` over
            // a contract that said ids while the server sent the objects, so
            // every narrowed connection came back as `[object Object]`. A bare
            // string is still read as an id with no ceiling of its own, which
            // is what an API older than per-layer ceilings sends.
            layers: Array.isArray(c.layers) ? c.layers.map(narrowingEntry) : [],
            permissions: Array.isArray(c.permissions) ? c.permissions.filter(isCeilingValue) : [],
            surface: c.surface === 'admin' ? ('admin' as const) : ('default' as const),
            createdAt: String(c.created_at),
            lastRefreshedAt: c.last_refreshed_at === null ? null : String(c.last_refreshed_at),
            revokedAt: c.revoked_at === null ? null : String(c.revoked_at),
          }
        }),
        accessTokenTtlSeconds: Number(body.access_token_ttl_seconds ?? 0),
      }
    },

    /**
     * End it. The refresh token goes immediately; an access token already
     * issued is verified against a key and keeps working until it expires, so
     * the answer carries how long that can still be.
     */
    end: async (id: string): Promise<{ accessTokenTtlSeconds: number } | undefined> => {
      try {
        const body = (await this.#request({
          method: 'DELETE',
          path: `/v1/oauth/consents/${encodeURIComponent(id)}`,
        })) as { access_token_ttl_seconds?: unknown }
        return { accessTokenTtlSeconds: Number(body.access_token_ttl_seconds ?? 0) }
      } catch (error) {
        if (error instanceof NacreError && error.status === 404) return undefined
        throw error
      }
    },
  }

  /**
   * Changes an agent proposed on the administrative MCP, waiting for you.
   *
   * Only your own session reaches these — a connected application's token is
   * answered as if the path did not exist, or a client could approve its own
   * proposals. `list` is short by construction: a proposal lives ten minutes.
   */
  readonly proposals = {
    list: async (): Promise<readonly Proposal[]> => {
      const body = (await this.#request({ method: 'GET', path: '/v1/proposals', retryable: true })) as { items?: unknown[] }
      return (body.items ?? []).map((raw) => {
        const p = raw as Record<string, unknown>
        const connection = (p.connection ?? {}) as Record<string, unknown>
        return {
          id: String(p.id),
          tool: String(p.tool),
          module: p.module == null ? null : String(p.module),
          summary: String(p.summary),
          details: Array.isArray(p.details)
            ? p.details.map((d) => {
                const detail = d as { label?: unknown; value?: unknown; text?: unknown }
                return { label: String(detail.label), value: String(detail.value), ...(detail.text === true ? { text: true } : {}) }
              })
            : [],
          createdAt: String(p.created_at),
          expiresAt: String(p.expires_at),
          connection: {
            id: String(connection.id),
            application: connection.application == null ? null : String(connection.application),
          },
        }
      })
    },

    /**
     * Press Apply. Not retried: applying twice is refused by the server, and a
     * retry after a lost answer would report `gone` for a change that happened.
     */
    apply: async (id: string): Promise<ProposalOutcome> => {
      try {
        const body = (await this.#request({ method: 'POST', path: `/v1/proposals/${encodeURIComponent(id)}/apply` })) as {
          result?: unknown
        }
        return { kind: 'applied', result: body.result }
      } catch (error) {
        // By the problem's type and not its status: a status is too coarse to
        // carry two facts, and `409` is not reserved to this one.
        if (error instanceof NacreError && error.type === 'https://nacre.work/errors/proposal-refused') {
          return { kind: 'refused', reason: error.detail }
        }
        if (error instanceof NacreError && error.status === 404) return { kind: 'gone' }
        throw error
      }
    },

    /** Press Cancel. `false` when it was no longer open. */
    cancel: async (id: string): Promise<boolean> => {
      try {
        await this.#request({ method: 'POST', path: `/v1/proposals/${encodeURIComponent(id)}/cancel` })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.status === 404) return false
        throw error
      }
    },
  }

  readonly serviceAccounts = {
    list: async (): Promise<readonly ServiceAccount[]> =>
      this.#listAll('/v1/service-accounts', accountFrom),

    /** The response carries the key. It is not recoverable afterwards. */
    create: async (name: string): Promise<CreatedServiceAccount> => {
      const body = (await this.#request({
        method: 'POST',
        path: '/v1/service-accounts',
        body: { name },
      })) as Record<string, unknown>

      return { ...accountFrom(body), key: String(body.key) }
    },

    revoke: async (id: string): Promise<boolean> => {
      try {
        await this.#request({
          method: 'DELETE',
          path: `/v1/service-accounts/${encodeURIComponent(id)}`,
        })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return false
        throw error
      }
    },
  }

  // ─── users and groups ────────────────────────────────────────────────────

  /**
   * The people in the organization.
   *
   * Every one of these needs `org_admin`, and a caller without it gets the same
   * `404` an unknown path gets — so `list` throwing `isNotFound` means "not an
   * administrator" as readily as it means anything else, which is invariant 4
   * working as intended rather than a shortcoming of this client.
   */
  readonly users = {
    list: async (): Promise<readonly User[]> => this.#listAll('/v1/users', userFrom),

    /** The response carries the password. It is not recoverable afterwards. */
    /**
     * `shared` is a credential more than one person will hold, such as a
     * published demo login. It has no `/v1/me` credential surface — no second
     * factor, no password change, no recovery link — because there is no "the
     * person" to hold one, and the first holder to enrol a factor would lock
     * out every other one. An administrator still resets its password, which is
     * how whoever published it rotates one. It cannot be changed afterwards.
     */
    create: async (
      email: string,
      role: 'member' | 'org_admin' = 'member',
      options: { shared?: boolean; externalId?: string } = {},
    ): Promise<CreatedUser> => {
      const body = (await this.#request({
        method: 'POST',
        path: '/v1/users',
        body: {
          email,
          role,
          ...(options.shared === true ? { shared: true } : {}),
          ...(options.externalId === undefined ? {} : { external_id: options.externalId }),
        },
      })) as Record<string, unknown>

      return { ...userFrom(body), password: String(body.password) }
    },

    /**
     * Change the role, the disabled state, the identity provider subject, or
     * any of them. `externalId: null` unlinks. False when there is no such user.
     */
    update: async (
      id: string,
      change: { role?: 'member' | 'org_admin'; disabled?: boolean; externalId?: string | null },
    ): Promise<boolean> => {
      const { externalId, ...rest } = change
      try {
        await this.#request({
          method: 'PATCH',
          path: `/v1/users/${encodeURIComponent(id)}`,
          body: { ...rest, ...(externalId === undefined ? {} : { external_id: externalId }) },
        })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return false
        throw error
      }
    },

    /**
     * Disable. The row is kept — see the endpoint's own note on why.
     *
     * `409` is not swallowed: refusing to strand an organization with no
     * administrator is a different answer from "no such user", and a caller
     * that cannot tell them apart would report the wrong one.
     */
    disable: async (id: string): Promise<boolean> => {
      try {
        await this.#request({ method: 'DELETE', path: `/v1/users/${encodeURIComponent(id)}` })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return false
        throw error
      }
    },

    /** A new password, shown once. `undefined` when there is no such user. */
    resetPassword: async (id: string): Promise<string | undefined> => {
      try {
        const body = (await this.#request({
          method: 'POST',
          path: `/v1/users/${encodeURIComponent(id)}/password`,
        })) as Record<string, unknown>
        return String(body.password)
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return undefined
        throw error
      }
    },
  }

  readonly groups = {
    list: async (): Promise<readonly Group[]> => this.#listAll('/v1/groups', groupFrom),

    create: async (name: string, options: { externalId?: string } = {}): Promise<Group> => {
      const body = (await this.#request({
        method: 'POST',
        path: '/v1/groups',
        body: { name, ...(options.externalId === undefined ? {} : { external_id: options.externalId }) },
      })) as Record<string, unknown>
      return groupFrom(body)
    },

    /**
     * Link a group to a directory's id — what SCIM addresses it by — or unlink
     * it with `null`. False when there is no such group.
     */
    link: async (id: string, externalId: string | null): Promise<boolean> => {
      try {
        await this.#request({
          method: 'PATCH',
          path: `/v1/groups/${encodeURIComponent(id)}`,
          body: { external_id: externalId },
        })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return false
        throw error
      }
    },

    remove: async (id: string): Promise<boolean> => {
      try {
        await this.#request({ method: 'DELETE', path: `/v1/groups/${encodeURIComponent(id)}` })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return false
        throw error
      }
    },

    /** Direct members. `undefined` when there is no such group — distinct from an empty one. */
    members: async (id: string): Promise<readonly GroupMember[] | undefined> => {
      try {
        const body = (await this.#request({
          method: 'GET',
          path: `/v1/groups/${encodeURIComponent(id)}/members`,
          retryable: true,
        })) as { items?: unknown[] }
        return (body.items ?? []).map((m) => memberFrom(m as Record<string, unknown>))
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return undefined
        throw error
      }
    },

    /** True once the member is in the group, whether this call is what put them there. */
    addMember: async (
      id: string,
      member: { type: 'user' | 'group'; id: string },
    ): Promise<boolean> => {
      try {
        await this.#request({
          method: 'POST',
          path: `/v1/groups/${encodeURIComponent(id)}/members`,
          body: { type: member.type, id: member.id },
        })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return false
        throw error
      }
    },

    removeMember: async (
      id: string,
      member: { type: 'user' | 'group'; id: string },
    ): Promise<boolean> => {
      try {
        await this.#request({
          method: 'DELETE',
          path:
            `/v1/groups/${encodeURIComponent(id)}/members/` +
            `${member.type}/${encodeURIComponent(member.id)}`,
        })
        return true
      } catch (error) {
        if (error instanceof NacreError && error.isNotFound) return false
        throw error
      }
    },
  }

  // ─── sign-in ─────────────────────────────────────────────────────────────

  /**
   * Email and password, and the refresh that keeps a session alive.
   *
   * **These are the one part of this client that does not use the constructor's
   * token**, because they are what a caller has instead of one. The client is
   * still constructed with a token — pass anything for a login-only client, or
   * construct a second one from the access token these return.
   *
   * That is deliberate rather than convenient. A client that swapped its own
   * credential when a login succeeded would make "which identity is this
   * object" depend on call history, and an application holding one for a
   * background job and one for a request would eventually get the wrong one.
   *
   * `undefined` for a refusal, and there is exactly one refusal: unknown
   * address, wrong password, wrong organization, disabled account, and an
   * account with no password set are one `401` with one message, in the same
   * time. Distinguishing them here would invent information the server refused
   * to give.
   */
  readonly auth = {
    login: async (input: {
      email: string
      password: string
      /**
       * A lookup key, never a claim. What goes into the issued token is the
       * organization on the row that authenticated — naming one you have no
       * account in is a refusal, not a token for either. Omit on a
       * single-organization installation.
       */
      organization?: string
    }): Promise<SignIn | undefined> => {
      const body = await this.#unauthorized<Record<string, unknown>>({
        method: 'POST',
        path: '/v1/auth/login',
        noAuthRefresh: true,
        body: {
          email: input.email,
          password: input.password,
          ...(input.organization === undefined ? {} : { organization: input.organization }),
        },
      })
      if (body === undefined) return undefined
      /*
       * Two shapes on one 200, and the union is the point.
       *
       * Where the account has a second factor the server answers with a
       * challenge instead of tokens — nothing was refused, the caller is being
       * asked for the rest. A client that read `access_token` and found nothing
       * would report a broken sign-in for a working one, so the type makes the
       * second case impossible to ignore.
       */
      if (body.second_factor_required === true) {
        return {
          secondFactorRequired: true,
          challenge: String(body.challenge ?? ''),
          expiresIn: Number(body.expires_in ?? 0),
        }
      }
      return enrolmentFrom(body) ?? tokensFrom(body)
    },

    /**
     * Finish a sign-in with a code from an authenticator, or a recovery code.
     *
     * Both go here. From outside they answer the same question, and a separate
     * method would say which one a person is using.
     */
    secondFactor: async (
      input: { challenge: string } & ({ code: string } | { assertion: WebAuthnAssertion }),
    ): Promise<Tokens | SecondFactorEnrolmentRequired | undefined> => {
      const body = await this.#unauthorized<Record<string, unknown>>({
        method: 'POST',
        path: '/v1/auth/second-factor',
        noAuthRefresh: true,
        body: {
          challenge: input.challenge,
          ...('code' in input ? { code: input.code } : { assertion: assertionJson(input.assertion) }),
        },
      })
      // A gate runs *after* the proof rather than instead of it, so one that
      // wanted a particular kind can still send this person to enrol.
      return body === undefined ? undefined : (enrolmentFrom(body) ?? tokensFrom(body))
    },

    /**
     * The options a browser needs before it can produce an assertion.
     *
     * Takes the sign-in challenge and nothing else, which is what keeps it from
     * being a way to ask which authenticators an address holds: reaching it
     * costs a correct password.
     */
    secondFactorWebAuthn: async (challenge: string): Promise<WebAuthnAssertionOptions | undefined> => {
      const body = await this.#unauthorized<Record<string, unknown>>({
        method: 'POST',
        path: '/v1/auth/second-factor/webauthn',
        noAuthRefresh: true,
        body: { challenge },
      })
      return body === undefined ? undefined : assertionOptionsFrom(body)
    },

    /**
     * Exchange a refresh token for a new pair. The old one stops working.
     *
     * **Replaying a spent refresh token revokes the whole family**, so a client
     * that retries this after a success signs its user out. Store the new
     * refresh token before doing anything else with the access token: by the
     * time the legitimate holder has exchanged one, a second presentation of it
     * is either a bug or a theft and there is no way to tell which.
     */
    refresh: async (
      refreshToken: string,
    ): Promise<Tokens | SecondFactorEnrolmentRequired | undefined> => {
      const body = await this.#unauthorized<Record<string, unknown>>({
        method: 'POST',
        path: '/v1/auth/refresh',
        noAuthRefresh: true,
        body: { refresh_token: refreshToken },
      })
      // A renewal is gated like a sign-in, so a policy turned on while somebody
      // is signed in reaches them here. The presented token is spent either
      // way: this is the end of that session, not something to retry.
      return body === undefined ? undefined : (enrolmentFrom(body) ?? tokensFrom(body))
    },

    /**
     * What this installation offers before anybody has signed in.
     *
     * One boolean, so a sign-in screen can leave the recovery link off where no
     * sender is configured rather than showing one that answers 404.
     */
    methods: async (): Promise<{
      passwordReset: boolean
      secondFactorKinds: readonly SecondFactorKind[]
    }> => {
      const body = (await this.#request({
        method: 'GET',
        path: '/v1/auth/methods',
        noAuthRefresh: true,
      })) as { password_reset?: boolean; second_factor_kinds?: readonly SecondFactorKind[] }
      return {
        passwordReset: body.password_reset === true,
        secondFactorKinds: body.second_factor_kinds ?? [],
      }
    },

    /**
     * Ask for a recovery link.
     *
     * Resolves whatever happened — an address with no account and one with an
     * account are the same answer, deliberately, and a client that reported
     * otherwise would be inventing the information the server refuses to give.
     */
    requestPasswordReset: async (email: string): Promise<void> => {
      await this.#request({
        method: 'POST',
        path: '/v1/auth/password-reset',
        noAuthRefresh: true,
        body: { email },
      })
    },

    /**
     * Spend a link and set the password. `false` for a refusal.
     *
     * A refusal is a link that never existed, one already spent, one that
     * expired, or an account disabled since it was sent — one answer for all
     * four. A password below the minimum raises instead, because that one is
     * about what the caller sent.
     */
    confirmPasswordReset: async (token: string, password: string): Promise<boolean> => {
      const done = await this.#unauthorized({
        method: 'POST',
        path: '/v1/auth/password-reset/confirm',
        noAuthRefresh: true,
        body: { token, password },
      })
      return done !== undefined
    },

    /** End the session. Idempotent: an already-revoked token is not an error. */
    logout: async (refreshToken: string): Promise<void> => {
      await this.#request({
        method: 'POST',
        path: '/v1/auth/logout',
        noAuthRefresh: true,
        body: { refresh_token: refreshToken },
      })
    },
  }

  /**
   * The caller's **own** second factor, and never anybody else's.
   *
   * There is no administrative counterpart to this on purpose. An administrator
   * resets a password; a second factor is a thing the person holds, and one an
   * administrator could enrol or remove would be a thing the account's
   * administrator holds instead.
   *
   * Every method here answers `404` on an installation with no
   * `NACRE_2FA_KEY`, for a service account, and for a delegation.
   */
  /**
   * Change the password of the account this token belongs to.
   *
   * The current one is required and is the only proof this takes — a session is
   * not enough, because changing the password is the first thing somebody with
   * a stolen session does. It is not `POST /v1/users/{id}/password`, which is
   * an administrator issuing a generated password to somebody else.
   *
   * **It ends every other session and returns the pair that replaces this
   * one.** A client holding a refresh token must adopt what comes back or its
   * next renewal fails: the old refresh token was revoked by the same
   * statement. `undefined` is a wrong current password — one refusal, and it is
   * a `403` rather than a `401` so that a client renewing on `401` does not
   * spend a refresh token retrying something retyping fixes.
   *
   * Answers `404` for a service account, which has no password, and for a
   * delegation, which was not approved to change how somebody signs in.
   */
  readonly changePassword = async (input: {
    currentPassword: string
    newPassword: string
  }): Promise<Tokens | SecondFactorEnrolmentRequired | undefined> => {
    try {
      const body = (await this.#request({
        method: 'POST',
        path: '/v1/me/password',
        body: { current_password: input.currentPassword, new_password: input.newPassword },
      })) as Record<string, unknown>
      // A gate can answer here too, and when it does **the password is already
      // changed** — the statement commits before the session is minted. So this
      // is a person with a new password and no session, and reporting it as a
      // failure would leave them typing the old one.
      return enrolmentFrom(body) ?? tokensFrom(body)
    } catch (error) {
      /*
       * Only the wrong-password refusal, matched on the problem **type** and
       * not on the status.
       *
       * Two different `403`s reach this endpoint now: this one, and a sign-in
       * gate refusing outright. They are different facts — one is fixed by
       * retyping and the other never is — and a status is too coarse to tell
       * them apart, which is what a stable `type` is for. Matching on `403`
       * alone would report a policy refusal as a typo, and the person would
       * retype a password that was correct all along.
       *
       * A password below the minimum is a `400` and raises, because that one is
       * about what the caller sent and a person retyping needs to be told which
       * rule they missed.
       */
      if (
        error instanceof NacreError &&
        error.status === 403 &&
        error.type !== 'https://nacre.work/errors/sign-in-refused'
      ) {
        return undefined
      }
      throw error
    }
  }

  readonly secondFactor = {
    /** What is enrolled, and how many recovery codes are left. */
    list: async (): Promise<{
      items: readonly SecondFactor[]
      recoveryCodesLeft: number
      kinds: readonly SecondFactorKind[]
    }> => {
      const body = (await this.#request({ method: 'GET', path: '/v1/me/second-factor' })) as {
        items?: readonly Record<string, unknown>[]
        recovery_codes_left?: number
        kinds?: readonly SecondFactorKind[]
      }
      return {
        items: (body.items ?? []).map(secondFactorFrom),
        recoveryCodesLeft: body.recovery_codes_left ?? 0,
        // What this installation can enrol. Empty rather than assumed, so a
        // screen reading it draws nothing rather than a control that 404s.
        kinds: body.kinds ?? [],
      }
    },

    /**
     * Begin enrolling one. The secret comes back **once**.
     *
     * Nothing counts it until `confirm`: a secret that has never produced a
     * correct code is one that did not reach an authenticator.
     */
    begin: async (input: { label?: string } = {}): Promise<BegunSecondFactor> => {
      const body = (await this.#request({
        method: 'POST',
        path: '/v1/me/second-factor',
        body: input.label === undefined ? {} : { label: input.label },
      })) as Record<string, unknown>
      return {
        id: String(body.id ?? ''),
        secret: String(body.secret ?? ''),
        otpauthUrl: String(body.otpauth_url ?? ''),
        label: String(body.label ?? ''),
      }
    },

    /** Confirm it, and take the recovery codes — they are printed once. */
    confirm: async (id: string, code: string): Promise<ConfirmedSecondFactor> => {
      const body = (await this.#request({
        method: 'POST',
        path: `/v1/me/second-factor/${encodeURIComponent(id)}/confirm`,
        body: { code },
      })) as Record<string, unknown>
      return confirmedFrom(body)
    },

    /**
     * Remove one, proving a current code.
     *
     * The code is not ceremony: taking the factor off is the first thing
     * somebody with a stolen session does.
     */
    remove: async (id: string, proof: string | WebAuthnAssertion): Promise<void> => {
      await this.#request({
        method: 'DELETE',
        path: `/v1/me/second-factor/${encodeURIComponent(id)}`,
        body: typeof proof === 'string' ? { code: proof } : { assertion: assertionJson(proof) },
      })
    },

    /**
     * Start enrolling a security key. Two calls, because a ceremony is two.
     *
     * The challenge has to exist on the server before the browser is asked for
     * anything, or the signature would be over a number the client chose.
     */
    beginWebAuthn: async (): Promise<WebAuthnRegistrationOptions> => {
      const body = (await this.#request({
        method: 'POST',
        path: '/v1/me/second-factor/webauthn',
      })) as Record<string, unknown>
      const rp = (body.rp ?? {}) as Record<string, unknown>
      const user = (body.user ?? {}) as Record<string, unknown>
      return {
        challenge: String(body.challenge ?? ''),
        rp: { id: String(rp.id ?? ''), name: String(rp.name ?? '') },
        user: {
          id: String(user.id ?? ''),
          name: String(user.name ?? ''),
          displayName: String(user.display_name ?? ''),
        },
        algorithms: (body.algorithms ?? []) as readonly number[],
        excludeCredentials: (body.exclude_credentials ?? []) as readonly string[],
        timeoutMs: Number(body.timeout_ms ?? 0),
      }
    },

    /**
     * Register what the authenticator signed, and take the recovery codes.
     *
     * No confirm step: producing the attestation *is* the proof, which is the
     * difference from TOTP, where a secret is handed over and only a code says
     * it landed.
     */
    finishWebAuthn: async (input: {
      challenge: string
      attestationObject: string
      clientDataJSON: string
      label?: string
    }): Promise<ConfirmedSecondFactor> => {
      const body = (await this.#request({
        method: 'POST',
        path: '/v1/me/second-factor/webauthn/finish',
        body: {
          challenge: input.challenge,
          attestation_object: input.attestationObject,
          client_data_json: input.clientDataJSON,
          ...(input.label === undefined ? {} : { label: input.label }),
        },
      })) as Record<string, unknown>
      return confirmedFrom(body)
    },

    /**
     * A challenge for proving possession to this surface, which `remove` needs.
     *
     * On an installation with no `NACRE_2FA_KEY` this is the only proof there
     * is, so without it a security key could be enrolled and never removed.
     */
    beginWebAuthnProof: async (): Promise<WebAuthnAssertionOptions> => {
      const body = (await this.#request({
        method: 'POST',
        path: '/v1/me/second-factor/webauthn/assert',
      })) as Record<string, unknown>
      return assertionOptionsFrom(body)
    },
  }

  // ─── the access log ──────────────────────────────────────────────────────

  /**
   * Read the journal back. Newest first, cursor-paged.
   *
   * **Two roles see two different logs, and it is not a parameter.**
   * `org_admin` sees its organization's log in full, including which documents
   * were read. `platform_admin` sees administrative actions and never that —
   * rule 2 applied to the journal, set by the server, so there is nothing to
   * pass here that would widen it. A `member` gets nothing.
   *
   * Reading it is itself recorded, as `audit.read`.
   *
   * JSONL and CSV exports exist on this endpoint through content negotiation
   * and are deliberately not wrapped: they are a stream to write to a file, not
   * a value to hold in memory, and a method returning one as a string would
   * invite exactly the use they exist to avoid.
   */
  readonly audit = {
    read: async (query: AuditQuery = {}): Promise<AuditPage> => {
      const search = new URLSearchParams()
      if (query.from !== undefined) search.set('from', query.from)
      if (query.to !== undefined) search.set('to', query.to)
      if (query.actorId !== undefined) search.set('actor_id', query.actorId)
      if (query.action !== undefined) search.set('action', query.action)
      if (query.result !== undefined) search.set('result', query.result)
      if (query.limit !== undefined) search.set('limit', String(query.limit))
      if (query.cursor !== undefined) search.set('cursor', query.cursor)

      const suffix = search.size === 0 ? '' : `?${search.toString()}`
      const body = (await this.#request({
        method: 'GET',
        path: `/v1/audit${suffix}`,
        retryable: true,
      })) as { items?: unknown[]; next_cursor?: unknown }

      const nextCursor = typeof body.next_cursor === 'string' ? body.next_cursor : undefined
      return {
        items: (body.items ?? []).map(auditRecordFrom),
        ...(nextCursor === undefined ? {} : { nextCursor }),
      }
    },
  }

  // ─── health ──────────────────────────────────────────────────────────────

  /** Liveness. Touches no dependency, and needs no token to be useful. */
  async health(): Promise<boolean> {
    try {
      await this.#request({ method: 'GET', path: '/v1/health', retryable: true })
      return true
    } catch {
      return false
    }
  }
}

/**
 * The permissions field, filtered to the three this model has.
 *
 * A server that grew a fourth would otherwise widen `Permission` at runtime
 * while the type said three, and every client comparison against the unknown
 * value would be silently false rather than a type error. An older server that
 * omits the field entirely answers the empty set, which is the safe reading:
 * "nothing is known to be permitted here" hides a control rather than offering
 * one that cannot work.
 */
function readPermissions(value: unknown): readonly Permission[] {
  const all: readonly Permission[] = ['read', 'write', 'admin']
  if (!Array.isArray(value)) return []
  return all.filter((p) => value.includes(p))
}

/** A ceiling value off the wire — a permission, or `skill`. Anything else is dropped, as `readPermissions` drops it. */
function isCeilingValue(value: unknown): value is CeilingValue {
  return value === 'read' || value === 'write' || value === 'admin' || value === 'skill'
}

/**
 * One entry of a connection's narrowing.
 *
 * `{ id, permissions? }` from this API; a bare id from one older than per-layer
 * ceilings, read as a layer that inherits the connection's ceiling — which is
 * exactly what a bare id meant there.
 */
function narrowingEntry(value: unknown): { id: string; permissions?: readonly CeilingValue[] } {
  if (typeof value === 'string') return { id: value }
  const entry = value as { id?: unknown; permissions?: unknown }
  return {
    id: String(entry.id),
    ...(Array.isArray(entry.permissions) ? { permissions: entry.permissions.filter(isCeilingValue) } : {}),
  }
}

function providerFrom(p: Record<string, unknown>): EmbeddingProvider {
  return {
    id: String(p.id),
    name: String(p.name ?? ''),
    model: String(p.model ?? ''),
    dimensions: Number(p.dimensions ?? 0),
    isDefault: p.is_default === true,
  }
}

function secondFactorFrom(f: Record<string, unknown>): SecondFactor {
  return {
    id: String(f.id),
    // Read rather than written. It said `'totp'` from when there was one kind,
    // which is the shape a mapper agreeing with itself always has: the field
    // was on the wire and this ignored it, so every security key would have
    // listed as an authenticator app.
    kind: f.kind === 'webauthn' ? 'webauthn' : 'totp',
    label: String(f.label ?? ''),
    createdAt: String(f.created_at ?? ''),
    lastUsedAt: f.last_used_at === null || f.last_used_at === undefined ? null : String(f.last_used_at),
  }
}

/** One writer for the wire shape, so two call sites cannot spell it two ways. */
function assertionJson(a: WebAuthnAssertion): Record<string, string> {
  return {
    credential_id: a.credentialId,
    authenticator_data: a.authenticatorData,
    client_data_json: a.clientDataJSON,
    signature: a.signature,
    challenge: a.challenge,
  }
}

/** And one reader, for the two paths that hand these out. */
function assertionOptionsFrom(body: Record<string, unknown>): WebAuthnAssertionOptions {
  return {
    challenge: String(body.challenge ?? ''),
    rpId: String(body.rp_id ?? ''),
    allowCredentials: (body.allow_credentials ?? []) as readonly string[],
    timeoutMs: Number(body.timeout_ms ?? 0),
  }
}

/**
 * The enrolment outcome, or nothing where the body is an ordinary one.
 *
 * One reader, because **four** endpoints can answer with this — sign-in, the
 * second half of a sign-in, a refresh and a password change — and four copies
 * of the same three fields is four chances for one of them to read
 * `access_token` off a body that has none and store `"undefined"` as a session.
 */
function enrolmentFrom(body: Record<string, unknown>): SecondFactorEnrolmentRequired | undefined {
  if (body.second_factor_enrolment_required !== true) return undefined
  return {
    secondFactorEnrolmentRequired: true,
    challenge: String(body.challenge ?? ''),
    expiresIn: Number(body.expires_in ?? 0),
    reason: String(body.reason ?? ''),
  }
}

/**
 * A confirmed enrolment: the codes, and the session where there is one.
 *
 * There is one only when the enrolment was reached through an **enrolment
 * challenge** — a client whose token is a session gets no pair back, because it
 * already has one. Both spellings go through here so the two routes that
 * confirm cannot drift.
 */
function confirmedFrom(body: Record<string, unknown>): ConfirmedSecondFactor {
  return {
    recoveryCodes: (body.recovery_codes ?? []) as readonly string[],
    tokens: typeof body.access_token === 'string' ? tokensFrom(body) : undefined,
  }
}

function tokensFrom(t: Record<string, unknown>): Tokens {
  return {
    accessToken: String(t.access_token),
    tokenType: String(t.token_type ?? 'Bearer'),
    expiresIn: Number(t.expires_in ?? 0),
    refreshToken: String(t.refresh_token),
  }
}

function reindexFrom(r: Record<string, unknown>): ReindexStatus {
  const check = r.check as Record<string, unknown> | null | undefined
  return {
    layerId: String(r.layer_id),
    status: r.status as ReindexStatus['status'],
    phase: r.phase === 'copying' ? 'copying' : 'embedding',
    currentVector: String(r.current_vector ?? ''),
    shadowVector: String(r.shadow_vector ?? ''),
    providerId: String(r.provider_id ?? ''),
    startedAt: String(r.started_at ?? ''),
    finishedAt: typeof r.finished_at === 'string' ? r.finished_at : null,
    total: Number(r.total ?? 0),
    done: Number(r.done ?? 0),
    failed: Number(r.failed ?? 0),
    progress: Number(r.progress ?? 0),
    error: typeof r.error === 'string' ? r.error : null,
    // `null` and not `undefined`. Absent would read as "this deployment does
    // not check recall"; null says "this migration has not been scored", which
    // for a layer with no reference set is the permanent and correct answer.
    check: check === null || check === undefined ? null : recallCheckFrom(check),
  }
}

/** The version a write starts from, as the query string a zip upload and a clear carry it in. */
function basedOnQuery(basedOn: number): string {
  return `?based_on=${String(basedOn)}`
}

/** `organization`, `installation`, or `layers/{id}` — the level as a path. */
function skillSegment(level: SkillLevel): string {
  return typeof level === 'string' ? level : `layers/${encodeURIComponent(level.layerId)}`
}

function skillEntryFrom(row: Record<string, unknown>): SkillEntry {
  return {
    level: row.level as SkillEntry['level'],
    layerId: (row.layer_id as string | null | undefined) ?? null,
    layerSlug: (row.layer_slug as string | null | undefined) ?? null,
    name: String(row.name ?? ''),
    description: String(row.description ?? ''),
    version: (row.version as number | null | undefined) ?? null,
    hasScripts: row.has_scripts === true,
    paths: (row.paths as string[] | undefined) ?? [],
  }
}

function skillVersionFrom(row: Record<string, unknown>): SkillVersion {
  return {
    version: Number(row.version),
    name: (row.name as string | null | undefined) ?? null,
    description: (row.description as string | null | undefined) ?? null,
    hasScripts: row.has_scripts === true,
    fileCount: Number(row.file_count ?? 0),
    principal: String(row.principal ?? ''),
    surface: row.surface as SkillVersion['surface'],
    connectionId: (row.connection_id as string | null | undefined) ?? null,
    byAgent: row.by_agent === true,
    restoredFrom: (row.restored_from as number | null | undefined) ?? null,
    createdAt: String(row.created_at ?? ''),
    ...(row.files === undefined ? {} : { files: row.files as SkillFiles }),
  }
}

/** The version a 409 says is current — a member of the problem document. */
function currentVersionOf(error: NacreError): number {
  const value = error.extensions.current_version
  return typeof value === 'number' ? value : 0
}

function recallCheckFrom(c: Record<string, unknown>): RecallCheck {
  const unresolved = Array.isArray(c.unresolved)
    ? c.unresolved.filter((u): u is string => typeof u === 'string')
    : []
  return {
    recall: Number(c.recall ?? 0),
    floor: Number(c.floor ?? 0),
    passed: c.passed === true,
    queries: Number(c.queries ?? 0),
    scores: (Array.isArray(c.scores) ? c.scores : []).map((s) => {
      const score = s as Record<string, unknown>
      return { queryId: String(score.query_id), recall: Number(score.recall ?? 0) }
    }),
    ...(unresolved.length === 0 ? {} : { unresolved }),
  }
}

function referenceQueryFrom(q: unknown): ReferenceQuery {
  const entry = q as Record<string, unknown>
  return {
    id: String(entry.id),
    query: String(entry.query ?? ''),
    expected: (Array.isArray(entry.expected) ? entry.expected : []).map(String),
  }
}

function auditRecordFrom(r: unknown): AuditRecord {
  const record = r as Record<string, unknown>
  const actor = (record.actor ?? {}) as Record<string, unknown>
  return {
    id: String(record.id),
    occurredAt: String(record.occurred_at ?? ''),
    actor: {
      type: String(actor.type ?? ''),
      id: typeof actor.id === 'string' ? actor.id : null,
      label: typeof actor.label === 'string' ? actor.label : null,
    },
    surface: typeof record.surface === 'string' ? record.surface : null,
    client: typeof record.client === 'string' ? record.client : null,
    action: String(record.action ?? ''),
    target: (record.target ?? {}) as Record<string, unknown>,
    result: record.result as AuditRecord['result'],
    detail: (record.detail ?? {}) as Record<string, unknown>,
    requestId: typeof record.request_id === 'string' ? record.request_id : null,
  }
}

function grantFrom(g: Record<string, unknown>): Grant {
  return {
    id: String(g.id),
    principalType: g.principal_type as Grant['principalType'],
    principalId: String(g.principal_id),
    scopeType: g.scope_type as Grant['scopeType'],
    scopeId: String(g.scope_id),
    permission: g.permission as Grant['permission'],
    effect: g.effect as Grant['effect'],
    source: String(g.source ?? ''),
  }
}

function userFrom(u: Record<string, unknown>): User {
  return {
    id: String(u.id),
    email: String(u.email ?? ''),
    role: (u.role as UserRole | undefined) ?? 'member',
    createdAt: String(u.created_at ?? ''),
    disabledAt: (u.disabled_at as string | null) ?? null,
    hasPassword: u.has_password === true,
    shared: u.shared === true,
    externalId: typeof u.external_id === 'string' ? u.external_id : null,
  }
}

function groupFrom(g: Record<string, unknown>): Group {
  return {
    id: String(g.id),
    name: String(g.name ?? ''),
    createdAt: String(g.created_at ?? ''),
    memberCount: Number(g.member_count ?? 0),
    externalId: typeof g.external_id === 'string' ? g.external_id : null,
  }
}

function memberFrom(m: Record<string, unknown>): GroupMember {
  return {
    type: m.type === 'group' ? 'group' : 'user',
    id: String(m.id),
    label: String(m.label ?? ''),
  }
}

function accountFrom(a: Record<string, unknown>): ServiceAccount {
  return {
    id: String(a.id),
    name: String(a.name ?? ''),
    keyPrefix: String(a.key_prefix ?? ''),
    createdAt: String(a.created_at ?? ''),
    lastUsedAt: (a.last_used_at as string | null) ?? null,
    revokedAt: (a.revoked_at as string | null) ?? null,
  }
}

/** The longest `Retry-After` a retry will sleep through before handing the error back. */
const MAX_RETRY_WAIT_MS = 10_000

/** `Retry-After` in seconds, either form RFC 9110 allows, or nothing. */
function retryAfterOf(header: string | null): { retryAfter?: number } {
  if (header === null) return {}
  const trimmed = header.trim()
  if (/^\d+$/.test(trimmed)) return { retryAfter: Number(trimmed) }
  const at = Date.parse(trimmed)
  if (Number.isNaN(at)) return {}
  return { retryAfter: Math.max(0, Math.ceil((at - Date.now()) / 1000)) }
}
